import { useEffect, useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import * as amplitude from '@amplitude/analytics-browser';

import {
  Button,
  Checkbox,
  getPaymentMethod,
  Input,
  isThousandOperand,
  Modal,
  Tooltip,
} from '@unified-checkout/design-system';
import { CircleInfo } from '@unified-checkout/design-system/icons/index';

import type { TSelectedMethod } from '@app/dashboard/types/innerLayout/payment_links/bulk/sections/payment-section';
import type { IPaymentMethodsProps } from '@app/dashboard/types/innerLayout/payment_links/single_quick_link';
import { QUERY_KEYS } from '@app/dashboard/constants/query-keys';
import { SINGLE_PAYMENT_LINKS_EVENTS } from '@app/dashboard/constants/tracking_events/payment_links/single';
import {
  SINGLE_PAYMENTS_FIELDS,
  SINGLE_PL_SECTIONS_DIMENSIONS,
} from '@app/dashboard/constants/payment-links/single-payments';
import { SHARED_EVENTS_PARAMS } from '@app/dashboard/constants/tracking_events/shared';
import { useTestLiveModeStore } from '@app/dashboard/store/testLiveMode';
import { PaymentMethodsTogglesGroup } from '@app/dashboard/components/inner_layout/PaymentMethodsTogglesGroup';
import { RemovableBadgesGroup } from '@app/dashboard/components/inner_layout/RemovableBadgesGroup';
import { getCurrentMode } from '@app/dashboard/utils/getCurrentMode';

import {
  badgeTextFormatter,
  fixedAmountValidator,
  preventExponentialNotationInput,
  remainingItemsBadgeTextFormatter,
} from '../../../utils';
import { SectionLoader } from '../../../Loader';
import { usePaymentSectionLogic } from '../../../hooks/usePaymentSectionLogic';

import styles from './payment-section.module.scss';

export const PaymentSection = ({
  fields,
  setFields,
  fullPageStatus,
  setFullPageStatus,
}: IPaymentMethodsProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isLive } = useTestLiveModeStore();

  const [fixedAmount, setFixedAmount] = useState<string>();

  const {
    isPreSelectedMethodsLoading,
    isPreSelectedMethodsError,

    currentCurrency,
    setCurrentCurrency,

    availableCurrencies,

    tempSelectedMethods,
    setTempSelectedMethods,

    savedMethods,
    setSavedMethods,

    isModalOpen,
    setIsModalOpen,

    modalActionBtns,
    isMethodsLoading,
    canChangeCurrency,
    orderedPaymentMethods,
    getFormattedCurrency,
    canViewPaymentIntegrations
  } = usePaymentSectionLogic({
    setFullPageStatus,
  });

  const fixedAmountChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const amount = e.target.value;
    const error = fixedAmountValidator(amount, currentCurrency as string);
    const toCentsFactor = isThousandOperand(currentCurrency as string)
      ? 1000
      : 100;
    let amountInCents = amount
      ? (parseFloat(amount) * toCentsFactor).toFixed(0)
      : undefined;

    if (error) {
      setFields((prev) => ({
        ...prev,
        [SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT]: {
          ...prev[SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT],
          errorMsg: error,
          isValid: false,
          value: amountInCents,
        },
      }));
    } else {
      setFields((prev) => ({
        ...prev,
        [SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT]: {
          ...prev[SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT],
          value: amountInCents,
          errorMsg: '',
          isValid: true,
        },
      }));
    }
    setFixedAmount(amount);
  };

  const handleOpenPaymentMethodsModal = () => {
    amplitude.track(SINGLE_PAYMENT_LINKS_EVENTS.PAYMENT_METHODS_MODAL_OPEN, {
      [SHARED_EVENTS_PARAMS.MODE]: getCurrentMode(isLive),
    });

    setIsModalOpen(true);
  };

  useEffect(() => {
    const paymentMethodsIds = Object.values(savedMethods || {}).map((method) =>
      String(method.id)
    );

    setFields((prev: any) => ({
      ...prev,
      [SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS]: {
        value: paymentMethodsIds,
        isValid: paymentMethodsIds.length > 0,
      },
    }));

    if (!!paymentMethodsIds.length) {
      setFields((prev) => ({
        ...prev,
        [SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS]: {
          ...prev[SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS],
          errorMsg: '',
        },
      }));
    } else {
      setFields((prev) => ({
        ...prev,
        [SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS]: {
          ...prev[SINGLE_PAYMENTS_FIELDS.PAYMENT_METHODS],
          errorMsg: t(
            'innerLayout.paymentLinksCreationPages.errors.selectAtLeast1Method'
          ),
        },
      }));
    }
  }, [savedMethods]);

  return fullPageStatus.loading ? (
    <SectionLoader {...SINGLE_PL_SECTIONS_DIMENSIONS.PAYMENT} />
  ) : (
    <div className={styles['payment-section']}>
      <div className={styles['payment-section__fixed-amount-input']}>
        <Input
          name="single-pl-fixed-amount-input"
          label={
            (
              <>
                {t('innerLayout.paymentLinksCreationPages.labels.amount')}
                {<span className={styles['required-sign']}> *</span>}
              </>
            ) as any
          }
          required
          type="number"
          placeholder={t(
            'innerLayout.paymentLinksCreationPages.placeholders.fixedAmountPlaceholder'
          )}
          leadingText={getFormattedCurrency(currentCurrency as string)}
          value={fixedAmount}
          status={
            fields?.[SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT]?.errorMsg
              ? 'error'
              : 'default'
          }
          error={fields?.[SINGLE_PAYMENTS_FIELDS.FIXED_AMOUNT]?.errorMsg}
          onChange={fixedAmountChangeHandler}
          onKeyDown={preventExponentialNotationInput}
        />
      </div>

      <div className={styles['payment-section__methods-box']}>
        <div className={styles['payment-section__methods-box__badges-box']}>
          {!isPreSelectedMethodsLoading && !isPreSelectedMethodsError && (
            <RemovableBadgesGroup
              list={savedMethods || []}
              badgesColor="grey"
              maxVisibleBadges={3}
              onChange={(updatedList) => {
                setSavedMethods(updatedList as TSelectedMethod);
                setTempSelectedMethods(updatedList as TSelectedMethod);
              }}
              badgeTextFormatter={badgeTextFormatter}
              remainingItemsBadgeTextFormatter={
                remainingItemsBadgeTextFormatter
              }
              emptyListText={t(
                'innerLayout.paymentLinksCreationPages.errors.selectAtLeast1Method'
              )}
            />
          )}
        </div>

        <div className={styles['payment-section__methods-box__actions']}>
          <Button
            size="md"
            variant="link"
            text={t(
              'innerLayout.paymentLinksCreationPages.sections.payment.managePaymentMethods'
            )}
            onClickHandler={handleOpenPaymentMethodsModal}
            disabled={isMethodsLoading || !canViewPaymentIntegrations}
          />
          {!canViewPaymentIntegrations && <Tooltip
            text={t(
              'innerLayout.paymentLinksCreationPages.errors.noPaymentIntegrationsPermission',
            )}
            triggerOn="hover"
            centerArrow
          >
            <div className={styles["payment-section__methods-box__actions__tooltip-icon"]}>
              {CircleInfo}
            </div>
          </Tooltip>}
        </div>

        <Checkbox
          type="checkbox"
          id="bulk-ql-save-methods"
          text={t(
            'innerLayout.paymentLinksCreationPages.sections.payment.saveMethods'
          )}
          onClickHandler={(checked) => {
            setFields((prev: any) => ({
              ...prev,
              [SINGLE_PAYMENTS_FIELDS.SAVE_SELECTIONS]: {
                ...prev[SINGLE_PAYMENTS_FIELDS.SAVE_SELECTIONS],
                value: checked,
              },
            }));
          }}
        />
      </div>

      <Modal
        position="center"
        open={isModalOpen}
        title={t('innerLayout.paymentLinksCreationPages.modals.methodsforLink')}
        btnsProps={modalActionBtns}
        closeOnClickOutside={false}
      >
        <div className={styles['payment-section__methods-modal']}>
          {
            <PaymentMethodsTogglesGroup
              loading={isMethodsLoading}
              currenciesDropdownProps={{
                disabled: !canChangeCurrency,
                label: t(
                  'innerLayout.paymentLinksCreationPages.labels.currency'
                ),
                defaultCurrency: currentCurrency,
                currenciesList: availableCurrencies,
                onClickHandler: (curr) => {
                  setCurrentCurrency(curr);
                  setTempSelectedMethods(undefined);
                  setSavedMethods(undefined);
                  queryClient.setQueryData(
                    [QUERY_KEYS.GET_MERCHANT_PRESELECTED_METHODS, isLive],
                    {}
                  );
                },
                formatCurrency: getFormattedCurrency,
              }}
              paymentMethodsList={orderedPaymentMethods}
              integrationsDropdownProps={{
                dropdown: {
                  width: 'full',
                  label: t(
                    'innerLayout.paymentLinksCreationPages.modals.integrationId'
                  ),
                },
                dropdownBtn: {
                  type: 'input',
                  text: (gatewayName) => {
                    const selectedGateway = tempSelectedMethods?.[gatewayName];
                    return (
                      selectedGateway?.integrationName ||
                      (selectedGateway?.id ? String(selectedGateway?.id) : '')
                    );
                  },
                  placeholder: t(
                    'innerLayout.paymentLinksCreationPages.sections.payment.selectIntegration'
                  ),
                },
                dropdownMenu: {
                  verticalPlacement: 'auto',
                },
              }}
              toggleBoxProps={{
                type: 'transparent box',
                onToggleChangeHandler: (
                  checked,
                  gatewayName,
                  integrations: any,
                  hasOneIntegration
                ) => {
                  if (checked) {
                    setTempSelectedMethods((prev) => ({
                      ...prev,
                      [gatewayName]: {
                        gatewayName: gatewayName,
                        ...(hasOneIntegration
                          ? {
                            id: integrations[0].id,
                            integrationName: integrations[0].integration_name,
                          }
                          : {
                            id: prev?.[gatewayName]?.id || null,
                            integrationName:
                              prev?.[gatewayName]?.integrationName || null,
                          }),
                      },
                    }));
                  } else {
                    setTempSelectedMethods((prev) => {
                      const { [gatewayName]: removed, ...rest } = prev || {};
                      return rest;
                    });
                  }
                },
                getToggleText: (
                  gatewayName,
                  integrations,
                  hasOneIntegration
                ) => {
                  return hasOneIntegration
                    ? `${getPaymentMethod(gatewayName).name} / ${integrations[0].integration_name || integrations[0].id
                    }`
                    : getPaymentMethod(gatewayName).name;
                },
                getToggleIcon: (gatewayName) =>
                  getPaymentMethod(gatewayName).icon as ReactElement<'svg'>,
              }}
              tempSelectedMethods={tempSelectedMethods}
              setTempSelectedMethods={setTempSelectedMethods}
            />
          }
        </div>
      </Modal>
    </div>
  );
};
